/**
 * Email Personalization Placeholder Utilities
 *
 * Handles placeholder syntax like [[FIRST_NAME]], [[LAST_NAME]], [[FULL_NAME]]
 * for email personalization in bulk mail.
 */

export const PLACEHOLDER_PATTERN = /\[\[(FIRST_NAME|LAST_NAME|FULL_NAME)\]\]/g;

export interface PlaceholderCustomer {
  first_name: string | null;
  last_name: string | null;
  full_name: string;
}

export interface ReplacementResult {
  text: string;
  hasFallbacks: boolean;
}

/**
 * Default fallback values when customer data is missing
 */
export const PLACEHOLDER_FALLBACKS = {
  FIRST_NAME: 'there',
  LAST_NAME: 'Customer',
  FULL_NAME: 'Valued Customer',
} as const;

/**
 * Placeholder definitions for UI display
 */
export const PERSONALIZATION_PLACEHOLDERS = [
  {
    value: '[[FIRST_NAME]]',
    label: "Customer's First Name",
    fallback: PLACEHOLDER_FALLBACKS.FIRST_NAME,
    description: 'Uses "there" if not available'
  },
  {
    value: '[[FULL_NAME]]',
    label: "Customer's Full Name",
    fallback: PLACEHOLDER_FALLBACKS.FULL_NAME,
    description: 'Uses "Valued Customer" if not available'
  },
] as const;

/**
 * Check if text contains any personalization placeholders
 */
export function hasPlaceholders(text: string): boolean {
  // Reset lastIndex to avoid stateful regex issues with global flag
  PLACEHOLDER_PATTERN.lastIndex = 0;
  return PLACEHOLDER_PATTERN.test(text);
}

/**
 * Replace placeholders in text with customer data or fallback values
 *
 * @example
 * replacePlaceholders("Hi [[FIRST_NAME]]!", { first_name: "Sarah", last_name: null, full_name: "Sarah" })
 * // Returns: { text: "Hi Sarah!", hasFallbacks: false }
 *
 * @example
 * replacePlaceholders("Hi [[FIRST_NAME]]!", { first_name: null, last_name: null, full_name: "" })
 * // Returns: { text: "Hi there!", hasFallbacks: true }
 */
export function replacePlaceholders(
  text: string,
  customer: PlaceholderCustomer
): ReplacementResult {
  let hasFallbacks = false;

  // Reset lastIndex to ensure consistent matching
  PLACEHOLDER_PATTERN.lastIndex = 0;

  const result = text.replace(PLACEHOLDER_PATTERN, (match, field) => {
    switch (field) {
      case 'FIRST_NAME':
        if (customer.first_name?.trim()) {
          return customer.first_name.trim();
        }
        hasFallbacks = true;
        return PLACEHOLDER_FALLBACKS.FIRST_NAME;

      case 'LAST_NAME':
        if (customer.last_name?.trim()) {
          return customer.last_name.trim();
        }
        hasFallbacks = true;
        return PLACEHOLDER_FALLBACKS.LAST_NAME;

      case 'FULL_NAME':
        if (customer.full_name?.trim()) {
          return customer.full_name.trim();
        }
        hasFallbacks = true;
        return PLACEHOLDER_FALLBACKS.FULL_NAME;

      default:
        return match;
    }
  });

  return { text: result, hasFallbacks };
}

/**
 * Style placeholders in preview HTML with amber background
 * Converts [[FIRST_NAME]] to styled <span> for visual distinction
 */
export function stylePreviewPlaceholders(text: string): string {
  PLACEHOLDER_PATTERN.lastIndex = 0;
  return text.replace(
    PLACEHOLDER_PATTERN,
    '<span style="background-color: #FEF3C7; padding: 1px 4px; border-radius: 3px; font-family: inherit;">[$1]</span>'
  );
}

/**
 * Get display name for a placeholder (for UI labels)
 */
export function getPlaceholderLabel(placeholder: string): string {
  const found = PERSONALIZATION_PLACEHOLDERS.find(p => p.value === placeholder);
  return found?.label || placeholder;
}
